import React, { useEffect, useMemo, useRef } from 'react';
import { CircleMarker, Marker, Polyline, Popup, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';

const MODE_STYLE = {
  WALK: { color: '#173b7a', weight: 6, opacity: 0.78, dashArray: '2 12' },
  BUS: { color: '#155eef', weight: 8, opacity: 0.9 },
  CART: { color: '#b7791f', weight: 8, opacity: 0.92 },
};

function pinIcon(label, variant) {
  return L.divIcon({
    className: 'journey-map-pin-shell',
    html: `<div class="journey-map-pin journey-map-pin-${variant}"><span>${label}</span></div>`,
    iconSize: [42, 50],
    iconAnchor: [21, 47],
    popupAnchor: [0, -44],
  });
}

function vehicleIcon(kind) {
  return L.divIcon({
    className: 'journey-map-live-shell',
    html: `<div class="journey-map-live"><i></i><strong>${kind === 'CART' ? 'C' : 'B'}</strong></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
}

export default function JourneyMapLayer({ plan }) {
  const map = useMap();
  const option = plan?.recommended;
  const lastFittedRouteRef = useRef('');

  const allCoordinates = useMemo(() => {
    if (!option) return [];
    return (option.segments || []).flatMap((segment) => segment.coordinates || []);
  }, [option]);

  const routeViewportKey = useMemo(() => {
    if (!option || !plan?.destination) return '';
    const destinationId = plan.destination.entityId || plan.destination.id ||
      `${Number(plan.destination.lat).toFixed(5)}:${Number(plan.destination.lng).toFixed(5)}`;
    const routeSignature = option.type === 'transit'
      ? `${option.routeCode || option.mode}:${option.boardStop?.id || ''}:${option.alightStop?.id || ''}`
      : `walk:${option.mode || option.type}`;
    return `${destinationId}|${routeSignature}|${plan?.navigationActive ? 'live' : 'panel'}`;
  }, [
    option?.alightStop?.id,
    option?.boardStop?.id,
    option?.mode,
    option?.routeCode,
    option?.type,
    plan?.destination,
    plan?.navigationActive,
  ]);

  useEffect(() => {
    if (!plan) lastFittedRouteRef.current = '';
  }, [plan]);

  useEffect(() => {
    if (!allCoordinates.length || !routeViewportKey) return;
    if (lastFittedRouteRef.current === routeViewportKey) return;
    lastFittedRouteRef.current = routeViewportKey;

    const bounds = L.latLngBounds(allCoordinates.map((point) => [point.lat, point.lng]));
    const compact = window.innerWidth <= 760;
    const fullMap = Boolean(plan?.navigationActive);
    map.fitBounds(bounds, {
      animate: true,
      duration: 0.6,
      maxZoom: 18,
      paddingTopLeft: fullMap
        ? (compact ? [28, 100] : [80, 80])
        : (compact ? [30, 130] : [500, 90]),
      paddingBottomRight: fullMap
        ? (compact ? [28, 120] : [80, 110])
        : (compact ? [30, 330] : [70, 90]),
    });
  }, [allCoordinates, map, plan?.navigationActive, routeViewportKey]);

  if (!option) return null;

  const firstSegment = option.segments?.[0];
  const lastSegment = option.segments?.[option.segments.length - 1];
  const origin = firstSegment?.from || plan.origin;
  const destination = lastSegment?.to || plan.destination;

  return (
    <>
      {(option.segments || []).map((segment, index) => {
        const positions = (segment.coordinates || []).map((point) => [point.lat, point.lng]);
        const style = MODE_STYLE[segment.mode] || MODE_STYLE.WALK;
        if (positions.length < 2) return null;
        return (
          <Polyline key={`${option.id}-${segment.mode}-${index}`} positions={positions} pathOptions={style}>
            <Tooltip sticky>{segment.instruction || `${segment.mode} · ${Math.round(segment.distanceM || 0)} m`}</Tooltip>
          </Polyline>
        );
      })}

      {origin && (
        <CircleMarker
          center={[origin.lat, origin.lng]}
          radius={8}
          pathOptions={{ color: '#ffffff', weight: 4, fillColor: '#173b7a', fillOpacity: 1 }}
        >
          <Tooltip direction="top" offset={[0, -8]}>Start</Tooltip>
        </CircleMarker>
      )}

      {option.boardStop && (
        <Marker position={[option.boardStop.lat, option.boardStop.lng]} icon={pinIcon('1', 'board')}>
          <Popup><strong>Board at {option.boardStop.shortName || option.boardStop.name}</strong></Popup>
        </Marker>
      )}

      {option.alightStop && (
        <Marker position={[option.alightStop.lat, option.alightStop.lng]} icon={pinIcon('2', 'alight')}>
          <Popup><strong>Get down at {option.alightStop.shortName || option.alightStop.name}</strong></Popup>
        </Marker>
      )}

      {destination && (
        <Marker position={[destination.lat, destination.lng]} icon={pinIcon('✓', 'destination')}>
          <Popup><strong>{plan.destination?.name || destination.name || 'Destination'}</strong></Popup>
        </Marker>
      )}

      {option.liveVehicle?.position && (
        <Marker
          position={[option.liveVehicle.position.lat, option.liveVehicle.position.lng]}
          icon={vehicleIcon(option.mode)}
          zIndexOffset={1200}
        >
          <Tooltip permanent direction="top" offset={[0, -18]} className="journey-live-tooltip">
            {option.mode === 'CART' ? 'Campus cart' : 'Campus bus'} · {option.liveVehicle.etaMinutes} min
          </Tooltip>
        </Marker>
      )}
    </>
  );
}

// import React, { useEffect, useMemo, useRef } from 'react';
// import { CircleMarker, Marker, Polyline, Popup, Tooltip, useMap } from 'react-leaflet';
// import L from 'leaflet';

// const MODE_STYLE = {
//   WALK: { color: '#173b7a', weight: 6, opacity: 0.78, dashArray: '2 12' },
//   BUS: { color: '#155eef', weight: 8, opacity: 0.9 },
//   CART: { color: '#b7791f', weight: 8, opacity: 0.92 },
// };

// function pinIcon(label, variant) {
//   return L.divIcon({
//     className: 'journey-map-pin-shell',
//     html: `<div class="journey-map-pin journey-map-pin-${variant}"><span>${label}</span></div>`,
//     iconSize: [42, 50],
//     iconAnchor: [21, 47],
//     popupAnchor: [0, -44],
//   });
// }

// function vehicleIcon(kind) {
//   return L.divIcon({
//     className: 'journey-map-live-shell',
//     html: `<div class="journey-map-live"><i></i><strong>${kind === 'CART' ? 'C' : 'B'}</strong></div>`,
//     iconSize: [40, 40],
//     iconAnchor: [20, 20],
//   });
// }

// export default function JourneyMapLayer({ plan }) {
//   const map = useMap();
//   const option = plan?.recommended;
//   const lastFittedRouteRef = useRef('');

//   const allCoordinates = useMemo(() => {
//     if (!option) return [];
//     return (option.segments || []).flatMap((segment) => segment.coordinates || []);
//   }, [option]);

//   const routeViewportKey = useMemo(() => {
//     if (!option || !plan?.destination) return '';
//     const destinationId = plan.destination.entityId || plan.destination.id ||
//       `${Number(plan.destination.lat).toFixed(5)}:${Number(plan.destination.lng).toFixed(5)}`;
//     return `${destinationId}|${option.id || option.type || option.mode}`;
//   }, [option?.id, option?.mode, option?.type, plan?.destination]);

//   useEffect(() => {
//     if (!plan) lastFittedRouteRef.current = '';
//   }, [plan]);

//   useEffect(() => {
//     if (!allCoordinates.length || !routeViewportKey) return;
//     if (lastFittedRouteRef.current === routeViewportKey) return;
//     lastFittedRouteRef.current = routeViewportKey;

//     const bounds = L.latLngBounds(allCoordinates.map((point) => [point.lat, point.lng]));
//     const compact = window.innerWidth <= 760;
//     map.fitBounds(bounds, {
//       animate: true,
//       duration: 0.6,
//       maxZoom: 18,
//       paddingTopLeft: compact ? [30, 130] : [500, 90],
//       paddingBottomRight: compact ? [30, 330] : [70, 90],
//     });
//   }, [allCoordinates, map, routeViewportKey]);

//   if (!option) return null;

//   const firstSegment = option.segments?.[0];
//   const lastSegment = option.segments?.[option.segments.length - 1];
//   const origin = firstSegment?.from || plan.origin;
//   const destination = lastSegment?.to || plan.destination;

//   return (
//     <>
//       {(option.segments || []).map((segment, index) => {
//         const positions = (segment.coordinates || []).map((point) => [point.lat, point.lng]);
//         const style = MODE_STYLE[segment.mode] || MODE_STYLE.WALK;
//         if (positions.length < 2) return null;
//         return (
//           <Polyline key={`${option.id}-${segment.mode}-${index}`} positions={positions} pathOptions={style}>
//             <Tooltip sticky>{segment.instruction || `${segment.mode} · ${Math.round(segment.distanceM || 0)} m`}</Tooltip>
//           </Polyline>
//         );
//       })}

//       {origin && (
//         <CircleMarker
//           center={[origin.lat, origin.lng]}
//           radius={8}
//           pathOptions={{ color: '#ffffff', weight: 4, fillColor: '#173b7a', fillOpacity: 1 }}
//         >
//           <Tooltip direction="top" offset={[0, -8]}>Start</Tooltip>
//         </CircleMarker>
//       )}

//       {option.boardStop && (
//         <Marker position={[option.boardStop.lat, option.boardStop.lng]} icon={pinIcon('1', 'board')}>
//           <Popup><strong>Board at {option.boardStop.shortName || option.boardStop.name}</strong></Popup>
//         </Marker>
//       )}

//       {option.alightStop && (
//         <Marker position={[option.alightStop.lat, option.alightStop.lng]} icon={pinIcon('2', 'alight')}>
//           <Popup><strong>Get down at {option.alightStop.shortName || option.alightStop.name}</strong></Popup>
//         </Marker>
//       )}

//       {destination && (
//         <Marker position={[destination.lat, destination.lng]} icon={pinIcon('✓', 'destination')}>
//           <Popup><strong>{plan.destination?.name || destination.name || 'Destination'}</strong></Popup>
//         </Marker>
//       )}

//       {option.liveVehicle?.position && (
//         <Marker
//           position={[option.liveVehicle.position.lat, option.liveVehicle.position.lng]}
//           icon={vehicleIcon(option.mode)}
//           zIndexOffset={1200}
//         >
//           <Tooltip permanent direction="top" offset={[0, -18]} className="journey-live-tooltip">
//             {option.liveVehicle.vehicleNo} · {option.liveVehicle.etaMinutes} min
//           </Tooltip>
//         </Marker>
//       )}
//     </>
//   );
// }
